import type {
  AdminChatReport,
  AdminChatReportPage,
  AdminCommunityReportGroup,
  AdminCommunityReportPage,
} from '@inflip/contracts/admin';
import { COMMUNITY_REPORT_REASONS } from '@inflip/contracts/user';
import {
  CHAT_REPORT_REASON_LABELS,
  CHAT_ROOM_TYPE_LABELS,
  REPORT_STATUS_LABELS,
  ReportStatus,
} from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { Button, Chip, EmptyState, ErrorBanner, PageHeader, PromptDialog } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';

type Tab = 'community' | 'chat';

const STATUS_CLS: Record<ReportStatus, string> = {
  PENDING: 'text-amber-600',
  ACTIONED: 'text-rose-600',
  DISMISSED: 'text-muted-foreground',
};

type DialogState =
  | null
  | { kind: 'remove' | 'dismiss'; group: AdminCommunityReportGroup }
  | { kind: 'chatActioned' | 'chatDismissed'; report: AdminChatReport };

const reasonLabel = (reason: string) =>
  COMMUNITY_REPORT_REASONS[reason as keyof typeof COMMUNITY_REPORT_REASONS] ?? reason;

/**
 * 신고 검토 — 커뮤니티 글·댓글(같은 대상은 묶어서 삭제·기각), 채팅 상대(신고마다 조치함·기각).
 * 이용 정지는 회원 상세에서 한다. 처리에 실패하면(다른 어드민이 먼저 처리) 목록을 다시 불러온다.
 */
export function ReportsPage() {
  const { session } = useAdminAuth();
  const token = session?.accessToken;
  const [tab, setTab] = useListViewState<Tab>('tab', 'community');
  const [status, setStatus] = useListViewState<ReportStatus>('status', ReportStatus.PENDING);
  const [page, setPage] = useListViewState('page', 1);
  const [community, setCommunity] = useState<AdminCommunityReportPage | null>(null);
  const [chat, setChat] = useState<AdminChatReportPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);

  const load = useCallback(() => {
    if (!token) return Promise.resolve();
    const query = { status, page, pageSize: 20 };
    const req =
      tab === 'community'
        ? api.listCommunityReports(token, query).then(setCommunity)
        : api.listChatReports(token, query).then(setChat);
    return req.catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [token, tab, status, page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      notifyPendingWorkChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setDialog(null);
    // 지금 상태가 그려질 때까지 버튼을 막아 둔다
    await load();
    setPending(false);
  }

  if (!session || !token) return null;
  const result = tab === 'community' ? community : chat;

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="신고"
        description="커뮤니티 글·댓글과 채팅 상대 신고를 검토해요. 커뮤니티는 같은 글·댓글의 신고를 묶어 한 번에 처리하고, 이용 정지는 회원 상세에서 해요."
      />

      <div className="mb-3 flex flex-wrap gap-1.5">
        {(
          [
            ['community', '커뮤니티'],
            ['chat', '채팅'],
          ] as const
        ).map(([key, label]) => (
          <Chip
            key={key}
            active={tab === key}
            onClick={() => {
              setPage(1);
              setTab(key);
            }}
          >
            {label}
          </Chip>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {Object.values(ReportStatus).map((s) => (
          <Chip
            key={s}
            active={status === s}
            onClick={() => {
              setPage(1);
              setStatus(s);
            }}
          >
            {REPORT_STATUS_LABELS[s]}
          </Chip>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}

      {result && result.items.length === 0 ? (
        <EmptyState message={`${REPORT_STATUS_LABELS[status]} 신고가 없어요.`} />
      ) : tab === 'community' ? (
        <div className="divide-y divide-border/60">
          {community?.items.map((g) => (
            <CommunityGroup
              key={`${g.targetType}:${g.targetId}`}
              group={g}
              busy={pending}
              onRemove={() => setDialog({ kind: 'remove', group: g })}
              onDismiss={() => setDialog({ kind: 'dismiss', group: g })}
            />
          ))}
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {chat?.items.map((r) => (
            <ChatReportRow
              key={r.id}
              report={r}
              busy={pending}
              onActioned={() => setDialog({ kind: 'chatActioned', report: r })}
              onDismiss={() => setDialog({ kind: 'chatDismissed', report: r })}
            />
          ))}
        </div>
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= result.totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {dialog?.kind === 'remove' && (
        <PromptDialog
          title={`이 ${dialog.group.targetType === 'POST' ? '글' : '댓글'}을 삭제할까요?`}
          description={`모든 사용자에게서 사라지고, 대기 중인 신고 ${dialog.group.reportCount}건이 조치함으로 닫혀요. 사유는 작성자에게 알림으로 전달돼요.`}
          placeholder="삭제 사유 (예: 광고·도배)"
          submitLabel="삭제"
          danger
          minLength={2}
          busy={pending}
          onSubmit={(note) =>
            void run(() =>
              api.removeReportedCommunityTarget(
                token,
                dialog.group.targetType,
                dialog.group.targetId,
                { note },
              ),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'dismiss' && (
        <PromptDialog
          title="신고를 기각할까요?"
          description={`글·댓글은 그대로 두고, 대기 중인 신고 ${dialog.group.reportCount}건을 기각으로 닫아요. 신고한 사람에게는 알리지 않아요.`}
          placeholder="기각 사유 (예: 운영 정책 위반 아님)"
          submitLabel="기각"
          minLength={2}
          busy={pending}
          onSubmit={(note) =>
            void run(() =>
              api.dismissCommunityReports(token, dialog.group.targetType, dialog.group.targetId, {
                note,
              }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
      {(dialog?.kind === 'chatActioned' || dialog?.kind === 'chatDismissed') && (
        <PromptDialog
          title={dialog.kind === 'chatActioned' ? '조치함으로 닫을까요?' : '신고를 기각할까요?'}
          description={
            dialog.kind === 'chatActioned'
              ? '이용 정지 같은 조치는 회원 상세에서 먼저 하고, 무엇을 했는지 적어 주세요.'
              : '문제가 없다고 보고 닫아요. 신고한 사람에게는 알리지 않아요.'
          }
          placeholder={
            dialog.kind === 'chatActioned' ? '처리 내용 (예: 피신고자 이용 정지)' : '기각 사유'
          }
          submitLabel={dialog.kind === 'chatActioned' ? '조치함' : '기각'}
          minLength={2}
          busy={pending}
          onSubmit={(note) =>
            void run(() =>
              api.resolveChatReport(token, dialog.report.id, {
                status:
                  dialog.kind === 'chatActioned' ? ReportStatus.ACTIONED : ReportStatus.DISMISSED,
                note,
              }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}

function CommunityGroup({
  group: g,
  busy,
  onRemove,
  onDismiss,
}: {
  group: AdminCommunityReportGroup;
  busy: boolean;
  onRemove(): void;
  onDismiss(): void;
}) {
  const pendingGroup = g.reports.some((r) => r.status === ReportStatus.PENDING);
  return (
    <section className="py-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold">
          {g.targetType === 'POST' ? '글' : '댓글'}
        </span>
        <span className="text-sm font-semibold">
          {g.targetType === 'POST' ? g.postTitle : `"${g.postTitle}"의 댓글`}
        </span>
        <span className="text-xs font-bold text-rose-600">신고 {g.reportCount}건</span>
        {g.deleted && <span className="text-xs text-muted-foreground">삭제됨</span>}
        <span className="ml-auto text-xs text-muted-foreground">
          최근 신고 {formatDateTime(g.lastReportedAt)}
        </span>
      </div>

      <p className="mt-2 whitespace-pre-line rounded-md bg-muted/50 px-3 py-2.5 text-sm">
        {g.currentContent ?? g.contentSnapshot}
      </p>
      {g.currentContent && g.currentContent !== g.contentSnapshot && (
        <p className="mt-1 text-xs text-muted-foreground">신고 당시 내용: {g.contentSnapshot}</p>
      )}

      <p className="mt-2 text-xs text-muted-foreground">
        작성자{' '}
        {g.author ? (
          <Link
            to="/users/$userId"
            params={{ userId: g.author.id }}
            className="font-semibold text-foreground hover:underline"
          >
            {g.author.name ?? '(탈퇴)'}
          </Link>
        ) : (
          '—'
        )}
      </p>

      <ul className="mt-2 space-y-1 text-xs">
        {g.reports.map((r) => (
          <li key={r.id} className="flex flex-wrap gap-x-2">
            <span className="font-semibold">{reasonLabel(r.reason)}</span>
            {r.detail && <span className="text-muted-foreground">— {r.detail}</span>}
            <span className="text-muted-foreground">
              · {r.reporter.name ?? '(탈퇴)'} · {formatDateTime(r.createdAt)}
            </span>
          </li>
        ))}
      </ul>

      {g.resolutionNote && (
        <p className="mt-2 text-xs text-muted-foreground">
          처리 {g.resolvedAt ? formatDateTime(g.resolvedAt) : ''} — {g.resolutionNote}
        </p>
      )}

      {pendingGroup && (
        <div className="mt-3 flex justify-end gap-1.5">
          <Button size="sm" variant="secondary" disabled={busy} onClick={onDismiss}>
            기각
          </Button>
          <Button size="sm" variant="danger" disabled={busy} onClick={onRemove}>
            {g.deleted ? '신고 닫기 (이미 삭제됨)' : '삭제'}
          </Button>
        </div>
      )}
    </section>
  );
}

function ChatReportRow({
  report: r,
  busy,
  onActioned,
  onDismiss,
}: {
  report: AdminChatReport;
  busy: boolean;
  onActioned(): void;
  onDismiss(): void;
}) {
  return (
    <section className="flex flex-wrap items-start gap-3 py-3.5">
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          <span className="font-semibold">{CHAT_REPORT_REASON_LABELS[r.reason]}</span>
          {r.detail && <span className="text-muted-foreground"> — {r.detail}</span>}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {r.reporter.name ?? '(탈퇴)'} →{' '}
          <Link
            to="/users/$userId"
            params={{ userId: r.targetUser.id }}
            className="font-semibold text-foreground hover:underline"
          >
            {r.targetUser.name ?? '(탈퇴)'}
          </Link>{' '}
          · {CHAT_ROOM_TYPE_LABELS[r.roomType]} ·{' '}
          <Link
            to="/chats/$roomId"
            params={{ roomId: r.roomId }}
            className="text-brand hover:underline"
          >
            대화 보기
          </Link>{' '}
          · {formatDateTime(r.createdAt)}
        </p>
        {r.resolutionNote && (
          <p className="mt-1 text-xs text-muted-foreground">
            <span className={`font-bold ${STATUS_CLS[r.status]}`}>
              {REPORT_STATUS_LABELS[r.status]}
            </span>{' '}
            {r.resolvedAt ? formatDateTime(r.resolvedAt) : ''} — {r.resolutionNote}
          </p>
        )}
      </div>
      {r.status === ReportStatus.PENDING && (
        <div className="flex gap-1.5">
          <Button size="sm" variant="secondary" disabled={busy} onClick={onDismiss}>
            기각
          </Button>
          <Button size="sm" disabled={busy} onClick={onActioned}>
            조치함
          </Button>
        </div>
      )}
    </section>
  );
}
